<template>
  <div class="page">

    <DeviationApprovalModal
      v-model="deviationModalShowing"
      :event-id="deviationModalId"
      @deviationApproved="getTotalDeviationCount"
    />

    <div class="top-section px-8 pt-4 pb-4">

      <!-- Top left calendar selection -->
      <div class="top-section-left">
        <div class="top-section-left-top">
          <!-- Calendar selection -->
          <div class="calendar-selection-box">
            <div class="calendar-selection-header">{{$t('Calendar')}}</div>
            <div class="calendar-selection-items">
              <div
                class="calendar-selection-item"
                :class="{
                  'calendar-selection-item-selected' : calendar.name === selectedCalendarType
                }"
                v-for="calendar in calendarTypes"
                :key="calendar.name"
                @click="selectCalendarType(calendar.name)"
              >
                <v-icon
                  class="calendar-selection-icon"
                  :class="{
                  'calendar-selection-icon-selected' : calendar.name === selectedCalendarType
                }"
                  size="50">{{ calendar.icon }}</v-icon>
                <span class="calendar-selection-name">
                  {{ $t(calendar.name) }}
                </span>
              </div>
            </div>
          </div>
          <!-- Appearance selection -->
          <div class="appearance-selection-container">
            <div class="appearance-selection-headline">{{$t('Appearance')}}</div>
            <div class="appearance-selection-item"
                 @click="selectedAppearance = 'Full'"
                 :class="{
                  'appearance-selection-item-selected' : selectedAppearance === 'Full'
                }">
              <v-icon class="appearance-selection-icon" :class="{
                  'appearance-selection-icon-selected' : selectedAppearance === 'Full'
                }">mdi-fullscreen</v-icon>
              {{$t('Full')}}
            </div>
            <div class="appearance-selection-item"
                 @click="selectedAppearance = 'Compact'"
                 :class="{
                  'appearance-selection-item-selected' : selectedAppearance === 'Compact'
                }">
              <v-icon class="appearance-selection-icon" :class="{
                  'appearance-selection-icon-selected' : selectedAppearance === 'Compact'
                }">mdi-fullscreen-exit</v-icon>
              {{$t('Compact')}}
            </div>
          </div>
        </div>

        <div class="d-flex align-center">
          <v-icon
            v-if="selectedCalendarType === 'Week'"
            @click="weekGoBack"
            large
          >mdi-arrow-left</v-icon>
          <div
            :class="selectedCalendarType === 'Day' ?
            'calendar-date-teller' :
            'calendar-date-teller-small'"
            class="mx-2"
          >{{ dateTeller }}</div>
          <v-icon
            v-if="!isSameDay(currentTime, weekShowDate) && selectedCalendarType === 'Week'"
            @click="weekGoForward"
            large
          >mdi-arrow-right</v-icon>
        </div>
      </div>

      <!-- Top right legend -->
      <div class="top-section-right">

        <div class="d-flex justify-center flex-column">
          <!-- Wide green indicator -->
          <div class="legend-item">
            <div class="legend-indicator-box d-flex justify-center align-center">
              <div class="legend-indicator-wide-green">
              </div>
            </div>
            <span>= {{ $t('Time frame') }}</span>
          </div>

          <!-- Wide red indicator -->
          <div class="legend-item">
            <div class="legend-indicator-box d-flex justify-center align-center">
              <div class="legend-indicator-wide-red">
              </div>
            </div>
            <span>= {{ $t('No click in time frame') }}</span>
          </div>

          <!-- Overdue indicator -->
          <div class="legend-item" style="height: 36px;">
            <div class="legend-indicator-box d-flex justify-center align-center">
              <div class="legend-indicator-overdue">

              </div>
            </div>
            <span>= {{ $t('Overdue timer') }}</span>
          </div>
        </div>
        <div class="d-flex justify-center flex-column">
          <!-- Check indicator -->
          <div class="legend-item">
            <div class="legend-indicator-box d-flex justify-center align-center">
              <div class="legend-indicator-green">
                <div style="color: white; font-size: 24px">&#10003;</div>
              </div>
            </div>
            <span>= {{ $t('Click in time frame') }}</span>
          </div>

          <!-- Red X indicator -->
          <div class="legend-item">
            <div class="legend-indicator-box d-flex justify-center align-center">
              <div class="legend-indicator-red">
                <div style="color: white; font-size: 24px">&#10005;</div>
              </div>
            </div>
            <span>= {{ $t('Click outside time frame') }}</span>
          </div>

          <!-- Approved indicator -->
          <div class="legend-item">
            <div class="legend-indicator-box d-flex justify-center align-center">
              <div class="legend-indicator-approved">
              </div>
            </div>
            <span>= {{ $t('Approved deviation') }}</span>
          </div>
        </div>

      </div>
    </div>

    <div class="calendar-section">
      <div v-if="loading && !tasks.length" class="loading-container">
        <KlikkitLogoLoader style="height: 250px; width: 250px; opacity: 0.2" />
      </div>
      <div
        v-if="!tasks.length && !loading"
        class="no-tasks-placeholder"
      >
        <div>
          {{$t('This citizen has no tasks')}}
        </div>
        <v-btn
          class="mt-4"
          color="primary"
          @click="$router
        .push({ name: 'member-show-add-task', params: { userId: $route.params.userId } })"
        >{{$t('Add task')}}</v-btn>
      </div>
      <day-calendar
        ref="calendar"
        v-if="selectedCalendarType === 'Day' && tasks.length"
        :tasks="daysToRender.find(day => isSameDay(day.date, currentTime)).tasks"
        :scale-down="selectedAppearance === 'Compact'"
        scale-up
        @deviationApproved="getTotalDeviationCount"
      ></day-calendar>
      <week-calendar
        ref="calendar"
        v-if="selectedCalendarType === 'Week' && tasks.length"
        :daysToRender="daysToRender"
        :tasks="tasks"
        scale-up
        :appearance="selectedAppearance"
        style="max-height: 100%;"
        :scale-down="selectedAppearance === 'Compact'"
        @deviationApproved="getTotalDeviationCount"
      ></week-calendar>
    </div>

    <div v-if="deviations.length || totalDeviationCount" class="bottom-section">
      <div
        class="bottom-section-content"
        :class="showDeviations ? 'bottom-section-content-show' :
        showDeviationsFirstTimeTriggered ? 'bottom-section-content-hide' : ''"
      >
        <div class="d-flex align-center">
          <v-btn @click="toggleShowDeviations">
            <div
              class="deviation-bubble"
            >
              {{ selectedCalendarType === 'Week' ?
              deviations.length :
              deviationsForToday.length }}
            </div>
            {{ !showDeviations ? $t(`Show deviations`) :
            $t(`Hide deviations`)}}
          </v-btn>
          <transition name="deviation-fade">
            <v-btn
              v-if="showDeviations"
              class="mx-2 deviation-switch-type"
              text
              @click="switchCalendarType"
            >
              <div
                class="deviation-bubble"
              >
                {{ selectedCalendarType === 'Week' ?
                deviationsForToday.length :
                deviations.length }}
              </div>

              {{ selectedCalendarType === 'Day' ?
              $t('Show week') :
              $t('Show today') }}</v-btn>
          </transition>
          <div class="deviation-total-count">
            {{ this.$t('X deviations in total', { count: totalDeviationCount || 0 }) }}
          </div>
        </div>
        <transition name="deviation-fade">
          <div v-if="showDeviations">
            <transition-group class="deviation-wrapper" name="deviations">
              <calendar-deviation
                v-for="deviation in selectedCalendarType === 'Week' ?
              deviations :
              deviationsForToday"
                :highlightId="highlightId"
                class="mr-2"
                :class="highlightId === deviation.id ? 'highlight-icon' : ''"
                :key="deviation.id"
                :deviation="deviation"
                @goToDeviation="goToDeviation"
                @resetHighlight="resetHighlight"
                @approve="openDeviationModal"
              ></calendar-deviation>
            </transition-group>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>

import {
  addDays,
  addSeconds,
  eachDayOfInterval,
  endOfDay,
  format,
  fromUnixTime,
  getTime,
  isAfter,
  isBefore,
  isSameDay,
  parseISO,
  startOfDay,
  subDays,
  subSeconds,
  isWithinInterval,
} from 'date-fns';
import DayCalendar from '@/modules/calendar/DayCalendar.vue';
import WeekCalendar from '@/modules/calendar/WeekCalendar.vue';
import CalendarDeviation from '@/modules/calendar/CalendarDeviation.vue';
import { getLanguagePackFromBrowser } from '../../globals/helpers/language';
import KlikkitLogoLoader from '../../modules/svg/KlikkitLogoLoader.vue';
import DeviationApprovalModal from '../../modules/calendar/DeviationApprovalModal.vue';

export default {
  name: 'member-show',
  components: {
    DeviationApprovalModal,
    DayCalendar,
    WeekCalendar,
    CalendarDeviation,
    KlikkitLogoLoader,
  },
  data() {
    return {
      loading: true,
      currentTime: Date.now(),
      currentTimeInterval: null,
      eventFetchInterval: null,
      showDeviations: false,
      showDeviationsFirstTimeTriggered: false,
      weekShowDate: Date.now(),
      totalDeviationCount: null,
      highlightId: null,
      selectedCalendarType: 'Day',
      selectedAppearance: 'Full',
      calendarTypes: [
        {
          name: 'Day',
          icon: 'mdi-calendar',
        },
        {
          name: 'Week',
          icon: 'mdi-calendar-range',
        },
        /* {
          name: 'Month',
          icon: 'mdi-calendar-month',
        }, */
      ],
      deviationModalShowing: false,
      deviationModalId: null,
    };
  },
  computed: {
    dateTeller() {
      if (this.selectedCalendarType === 'Week') {
        return `${format(
          getTime(this.allDates[0]),
          'dd. MMMM yyyy', { locale: getLanguagePackFromBrowser() },
        )} - ${
          format(
            getTime(this.allDates[6]),
            'dd. MMMM yyyy', { locale: getLanguagePackFromBrowser() },
          )}`;
      }
      return format(
        getTime(this.currentTime),
        'dd. MMMM yyyy', { locale: getLanguagePackFromBrowser() },
      );
    },
    allTasks() {
      return this.$Task.query().with('events')
        .where((task) => task.user === this.$route.params.userId).get();
    },
    tasks() {
      return this.$Task.query().with('events')
        .where((task) => task.user === this.$route.params.userId).get()
        .map((task) => ({
          ...task,
          schedule: task.schedule ? {
            ...task.schedule,
            intervals: task.schedule.intervals.map((item) => ({
              ...item,
              timestamp: fromUnixTime(item.timestamp),
            })),
          } : null,
          events: task.events.map((event) => {
            if (event.click) {
              return {
                ...event,
                timestamp: fromUnixTime(event.timestamp),
                click: {
                  ...event.click,
                  timestamp: fromUnixTime(event.click.timestamp),
                },
              };
            }
            return {
              ...event,
              timestamp: fromUnixTime(event.timestamp),
            };
          }),
        }));
    },
    daysToRender() {
      const mappedTasksWithOverdueByDate = this.tasks.reduce((acc, cur) => {
        acc[cur.uuid] = { name: cur.name, dates: [] };
        return acc;
      }, {});
      this.tasks.forEach((task) => {
        if (task.type === 'timerTask' || task.type === 'bradenTask') {
          // generate overdue from events
          task.events.forEach((event) => {
            if (event.type === 'overdue') {
              const daysWithOverdue = eachDayOfInterval(
                { start: event.timestamp, end: event.click.timestamp },
              );
              daysWithOverdue.forEach((date) => {
                const payload = {
                  type: 'generatedOverdue',
                };
                if (isSameDay(event.timestamp, date)) {
                  payload.start = event.timestamp;
                } else {
                  payload.start = startOfDay(date);
                }

                if (isSameDay(event.click.timestamp, date)) {
                  payload.end = event.click.timestamp;
                } else {
                  payload.end = endOfDay(date);
                }
                mappedTasksWithOverdueByDate[task.uuid].dates.push(payload);
              });
            }
          });
          // generate overdue from expected clicks
          if (isBefore(task.schedule.intervals[0].timestamp, this.currentTime)) {
            const daysWithOverdue = eachDayOfInterval({
              start: task.schedule.intervals[0].timestamp,
              end: this.currentTime,
            });
            daysWithOverdue.forEach((date) => {
              const payload = {
                type: 'generatedOverdue',
              };

              if (isSameDay(task.schedule.intervals[0].timestamp, date)) {
                payload.start = task.schedule.intervals[0].timestamp;
              } else {
                payload.start = startOfDay(date);
              }

              if (isSameDay(this.currentTime, date)) {
                payload.end = this.currentTime;
              } else {
                payload.end = endOfDay(date);
              }

              mappedTasksWithOverdueByDate[task.uuid].dates.push(payload);
            });
          }
        }
      });
      const mergedTasks = this.tasks.map((task) => ({
        ...task,
        events: [...task.events, ...mappedTasksWithOverdueByDate[task.uuid].dates],
      }));

      const data = this.allDates.map((date) => ({
        date,
        tasks: mergedTasks.map((task) => ({
          ...task,
          createdAt: parseISO(task.createdAt),
          events: task.events.reduce((acc, cur) => {
            if (isSameDay(
              date,
              cur?.click?.timestamp
                ? cur.click.timestamp
                : cur.timestamp,
            ) || (cur.type === 'generatedOverdue' && isSameDay(cur.start, date))) {
              acc.push(cur);
              return acc;
            }
            return acc;
          }, []),
        })).map((task, index) => {
          // create object for single task
          const newTask = {
            name: task.name,
            isPaused: task.isPaused,
            type: task.type,
            id: task.uuid,
            description: task.description,
            createdAt: task.createdAt,
            taskOrder: index,
            events: task.events,
          };

          if (newTask.type === 'timerTask' || newTask.type === 'bradenTask') {
            newTask.interval = task?.schedule?.intervals[0]?.interval
              ? task.schedule.intervals[0].interval : null;
            newTask.nextClick = task?.schedule?.intervals[0]?.timestamp
              ? task.schedule.intervals[0].timestamp : null;
          }

          // set zones
          const previousZones = (task.type === 'scheduledTask'
            || task.type === 'scheduledOnDemandTask') ? task.events
              .filter((event) => (event.type === 'fulfilled' || event.type === 'missed')
              && isSameDay(event.timestamp, date))
              .map((event) => ({
                eventId: event.id,
                startTime: subSeconds(event.timestamp, task.schedule
                  ? task.schedule.tolerance : 300),
                endTime: addSeconds(event.timestamp, task.schedule
                  ? task.schedule.tolerance : 300),
                timestamp: event.timestamp,
                type: event.type,
                fill: (event.type === 'fulfilled') ? '#0dac30' : '#FF454B',
                events: [event],
              })) : [];
          let futureZones;
          if (task.type === 'timerTask' || task.type === 'bradenTask') {
            futureZones = task.schedule.intervals
              .filter((event) => isSameDay(event.timestamp, date)
              || isSameDay(subSeconds(event.timestamp, event.interval), date)).map((event) => {
                let endTime = event.timestamp;
                let startTime = subSeconds(event.timestamp, event.interval);
                if (!isSameDay(endTime, date)) {
                  endTime = endOfDay(date);
                }
                if (!isSameDay(startTime, date)) {
                  startTime = startOfDay(date);
                }
                return {
                  startTime,
                  endTime,
                  timestamp: event.timestamp,
                  type: 'future',
                  fill: '#0dac30',
                  events: [],
                };
              });
          } else {
            futureZones = task.schedule ? task.schedule.intervals
              .filter((event) => isSameDay(event.timestamp, date)
                && isAfter(addSeconds(event.timestamp, task.schedule.tolerance), this.currentTime))
              .map((event) => ({
                startTime: subSeconds(event.timestamp, task.schedule.tolerance),
                endTime: addSeconds(event.timestamp, task.schedule.tolerance),
                timestamp: event.timestamp,
                type: 'future',
                fill: '#0dac30',
                events: [],
              })) : [];
          }

          newTask.zones = previousZones.concat(futureZones);

          // set markers
          const markers = task.events.reduce((acc, cur) => {
            // if event is not for second date, filter it out
            if (!isSameDay(cur.click ? cur.click.timestamp : cur.timestamp, date)) {
              return acc;
            }
            // cur.type !== 'fullOverdue') return acc;
            // if event.type is "missed" filter it out
            if (cur.type === 'generatedOverdue') return acc;
            if (cur.type === 'missed' && !cur.approvedBy) return acc;
            if (task.type === 'onDemandTask' && cur.type === 'extra') return acc;

            // Look for a marker within time interval for grouping
            const existingMarkerIndex = acc.findIndex((marker) => isWithinInterval(
              cur.timestamp, { start: marker.startTime, end: marker.endTime },
            ));

            if (existingMarkerIndex >= 0 && cur.type !== 'missed') {
              if (acc[existingMarkerIndex].events.length) {
                acc[existingMarkerIndex].type = 'mixed';
              } else {
                acc[existingMarkerIndex].type = cur.type;
              }
              if (!acc[existingMarkerIndex].events.find((event) => event.id === cur.id)) {
                acc[existingMarkerIndex].events.push(cur);
              }
              const markerSpecs = this.getMarkerSpecs(acc[existingMarkerIndex].events);
              acc[existingMarkerIndex].fill = markerSpecs.fill;
              acc[existingMarkerIndex].icon = markerSpecs.icon;
              acc[existingMarkerIndex].hasNotes = markerSpecs.hasNotes;
              acc[existingMarkerIndex].stroke = markerSpecs.stroke;
              acc[existingMarkerIndex].backgroundFill = markerSpecs.backgroundFill;
            } else {
              const markerSpecs = this.getMarkerSpecs([cur]);
              acc.push({
                type: cur.type,
                timestamp: cur.click ? cur.click.timestamp : cur.timestamp,
                events: [cur],
                fill: markerSpecs.fill,
                icon: markerSpecs.icon,
                startTime: subSeconds(cur.click ? cur.click.timestamp : cur.timestamp, 1800),
                endTime: addSeconds(cur.click ? cur.click.timestamp : cur.timestamp, 1800),
                hasNotes: markerSpecs.hasNotes,
                stroke: markerSpecs.stroke,
                backgroundFill: markerSpecs.backgroundFill,
              });
            }

            return acc;
            // missed types from previousZones are filtered out to not create markers
          }, previousZones.filter((zone) => zone.type !== 'missed'));
          newTask.markers = markers;
          newTask.overdues = task.events.filter((item) => item.type === 'generatedOverdue');
          return newTask;
        }),
      })).reverse();

      return data;
      // This is reversed because we render the days from the bottom up
      // This is to ensure that the highest calender days are rendered last so that ->
      // The timestamps from above days can overflow the days below it
    },
    allDates() {
      const today = this.selectedCalendarType === 'Week' ? this.weekShowDate : this.currentTime;
      return [
        subDays(today, 5),
        subDays(today, 4),
        subDays(today, 3),
        subDays(today, 2),
        subDays(today, 1),
        fromUnixTime(today / 1000),
        addDays(today, 1),
      ];
    },
    deviationsForToday() {
      return this.deviations.filter((deviation) => (deviation.click
        ? isSameDay(fromUnixTime(deviation.click.timestamp), this.currentTime)
        : isSameDay(fromUnixTime(deviation.timestamp), this.currentTime)));
    },
    deviations() {
      return this.$Event.query().where((event) => event.type !== 'fulfilled' && !event.approvedBy).withAll().get()
        .sort((a, b) => {
          if (a.click) {
            if (b.click) {
              if (isBefore(a.click.timestamp, b.click.timestamp)) {
                return 1;
              }
              return -1;
            }

            if (isBefore(a.click.timestamp, b.timestamp)) {
              return 1;
            }
            return -1;
          }
          if (b.click) {
            if (isBefore(a.timestamp, b.click.timestamp)) {
              return 1;
            }
            return -1;
          }

          if (isBefore(a.timestamp, b.timestamp)) {
            return 1;
          }
          return -1;
        });
    },
  },
  watch: {
    showDeviations: {
      handler(newVal) {
        if (!newVal) {
          this.resetHighlight();
        }
      },
    },
  },
  created() {
    this.$Event.deleteAll();
    const from = startOfDay(subDays(Date.now(), 5));
    const to = endOfDay(Date.now());
    this.getTotalDeviationCount();
    this.$Task.api().fetchByUserAndGroup(this.$route.params.userId, this.$route.params.groupName)
      .then((response) => {
        const eventCalls = [];
        response.response.data.forEach((task) => {
          eventCalls.push(this.$Event.api().fetchByTaskId(task.uuid, from, to));
        });

        Promise.all(eventCalls).then(() => {
          this.loading = false;
        });
      });
    this.eventFetchInterval = setInterval(() => {
      this.loading = true;
      const eventCalls = [];
      this.tasks.forEach((task) => {
        eventCalls.push(this.$Event.api().fetchByTaskId(task.uuid, from, to));
      });
      Promise.all(eventCalls).then(() => {
        this.loading = false;
      });
    }, 60000);
    this.currentTimeInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 30000);
  },
  beforeDestroy() {
    clearInterval(this.eventFetchInterval);
    clearInterval(this.currentTimeInterval);
  },
  methods: {
    isSameDay,
    getTotalDeviationCount() {
      this.$MemberDeviation.api()
        .fetchByGroupAndUser(this.$route.params.groupName, this.$route.params.userId)
        .then((response) => {
          this.totalDeviationCount = response.response.data.deviations;
        });
    },
    weekGoForward() {
      this.weekShowDate = addDays(this.weekShowDate, 7);
    },
    weekGoBack() {
      this.weekShowDate = subDays(this.weekShowDate, 7);
      this.allTasks.forEach((task) => {
        this.$Event.api()
          .fetchByTaskId(task.uuid, startOfDay(this.allDates[0]), endOfDay(this.allDates[6]));
      });
    },
    selectCalendarType(name) {
      this.highlightId = null;
      this.selectedCalendarType = name;
    },
    switchCalendarType() {
      if (this.selectedCalendarType === 'Day') {
        this.selectedCalendarType = 'Week';
      } else {
        this.selectedCalendarType = 'Day';
      }
    },
    goToDeviation(id) {
      this.highlightId = id;
      this.$refs.calendar.goToDeviation(id);
    },
    resetHighlight() {
      this.highlightId = null;
      this.$refs.calendar.goToDeviation(null);
    },
    toggleShowDeviations() {
      this.showDeviationsFirstTimeTriggered = true;
      this.showDeviations = !this.showDeviations;
    },
    getMarkerSpecs(events) {
      let payload = {};

      const markerStyles = {
        fulfilled: { fill: '#0dac30', icon: '&#10003' },
        missed: { fill: '#FF454B', icon: '&#10005' },
        extra: { fill: '#FF454B', icon: '&#10005' },
        overdue: { fill: '#FF454B', icon: '&#10005' },
        mixedGood: { fill: '#0dac30', icon: null },
        mixedBad: { fill: '#FF454B', icon: null, backgroundFill: '#0dac30' },
        mixedApproved: { fill: '#0dac30', icon: null, stroke: '#FF454B' },
        approved: { fill: '#0dac30', icon: null, stroke: '#FF454B' },
      };

      if (events.length === 1) {
        const { type } = events[0];
        const approved = events[0].approvedBy;
        if (approved) {
          payload = { ...markerStyles.approved };
        } else {
          payload = { ...markerStyles[type] };
        }
      } else if (events.find((event) => event.type !== 'fulfilled' && !event.approvedBy)) {
        payload = { ...markerStyles.mixedBad };
      } else if (events.find((event) => event.type !== 'fulfilled' && event.approvedBy)) {
        payload = { ...markerStyles.mixedApproved };
      } else {
        payload = { ...markerStyles.mixedGood };
      }

      // check if notes exist
      payload.hasNotes = events.reduce((acc, cur) => {
        if (cur.notes.length) {
          return true;
        }
        return acc;
      }, false);

      return payload;
    },
    openDeviationModal(id) {
      this.deviationModalShowing = true;
      this.deviationModalId = id;
    },
  },
};

</script>

<style scoped lang="scss">
  .page {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .top-section {
    display: flex;
    justify-content: space-between;
  }

  .top-section-left {
    display: flex;
    flex-direction: column;
  }

  .top-section-left-top {
    display: flex;
    justify-content: flex-start;
    margin-bottom: 6px;
  }

  .top-section-right {
    display: flex;
  }

  .calendar-section {
    background-color: #fff;
    flex: 1;
    overflow: auto;
  }

  .calendar-selection-box {
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    margin-right: 32px;
  }

  .calendar-selection-header {
    padding: 4px 8px;
    color: $primaryDark;
    font-weight: 500;
  }

  .calendar-selection-items {
    display: flex;
  }

  .calendar-selection-item {
    color: #aaa !important;
    transition: all 0.2s;
    border-bottom: 4px solid transparent;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 0 24px;
    font-weight: 500;
    border-radius: 8px;
    margin-right: 4px;

    &-selected {
      background-color: rgba(132, 100, 255, 0.1);
      color: $primary !important;
    }

    &:not(.calendar-selection-item-selected):hover {
      color: rgba(132, 100, 255, 0.1);
      background-color: rgba(132, 100, 255, 0.05);
    }

    // remove v-btn focus effect
    &::after {
      opacity: 0 !important;
    }
  }

  .calendar-selection-icon {
    margin-top: 16px;
  }

  .calendar-selection-icon-selected {
    color: $primary;
  }

  .calendar-selection-name {
    font-size: 14px;
    margin-top: -4px;
  }

  .calendar-date-teller {
    font-size: 30px;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.5)
  }

  .calendar-date-teller-small {
    font-size: 22px;
    font-weight: bold;
    color: rgba(0, 0, 0, 0.5)
  }

  .appearance-selection-container {
    padding: 5px;
  }

  .appearance-selection-headline {
    color: $primaryDark;
    font-weight: 500;
    margin-bottom: 4px;
  }

  .appearance-selection-icon {
    margin-right: 4px;

    &-selected {
      color: $primary;
    }
  }

  .appearance-selection-item {
    padding: 4px 12px;
    cursor: pointer;
    font-weight: 500;
    border-radius: 4px;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    transition: all 0.2s;

    &-selected {
      background-color: rgba(132, 100, 255, 0.1);
      color: $primary;
    }

    &:not(.appearance-selection-item-selected):hover {
      background-color: rgba(132, 100, 255, 0.05);
    }
  }

  .legend-indicator-box {
    width: 70px;
    margin-right: 6px;
  }

  .legend-indicator, .legend-indicator-check,
  .legend-indicator-green, .legend-indicator-red,
  .legend-indicator-approved {
    width: 36px;
    height: 36px;
    border-radius: 18px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .legend-indicator-wide-green {
    width: 70px;
    height: 36px;
    border-radius: 18px;
    background-color: rgba(0,205,43, 0.3);
  }

  .legend-indicator-wide-red {
    width: 70px;
    height: 36px;
    border-radius: 18px;
    background-color: rgba(255, 69, 75, 0.3);
  }

  .legend-indicator-overdue {
    width: 70px;
    height: 18px;
    border-radius: 9px;
    background-color: #FF454B;
  }

  .legend-indicator-green {
    background-color: #0dac30;
  }

  .legend-indicator-approved {
    background-color: #0dac30;
    border: 4px solid #FF454B;
  }

  .legend-indicator-red {
    background-color: #FF454B;
  }

  .legend-item {
    display: flex;
    margin-bottom: 10px;
    align-items: center;
    font-size: 14px;
  }

  .bottom-title {
    border-bottom: 1px solid #ccc;
    font-weight: bold;
    color: #797979;
    padding-left: 16px;
    padding-bottom: 4px;
    margin-bottom: 16px;
  }

  .deviation-wrapper {
    overflow-x: auto;
    width: calc(100vw - 300px - 16px);
    white-space: nowrap;
    padding: 6px 0px;
    display: flex;
  }

  .bottom-section {
    width: calc(100vw - 300px);
    min-height: 52px;
    display: flex;
    padding: 8px;
    align-items: center;
    position: relative;
  }

  .bottom-section-content {
    position: absolute;
    left: 0px;
    bottom: 0px;
    height: 52px;
    background-color: $primaryDark;
    align-items: center;
    padding: 8px;
    width: 100%;
    overflow: hidden;
  }

  @keyframes bottom-section-show {
    0% { height: 52px; }
    30% { height: 42px; }
    100% { height: 205px; }
  }

  @keyframes bottom-section-hide {
    0% { height: 205px; }
    30% { height: 215px; }
    100% { height: 52px; }
  }

  .bottom-section-content-show {
    animation: 0.3s bottom-section-show forwards;
  }

  .bottom-section-content-hide {
    animation: 0.3s bottom-section-hide forwards;
  }

  // deviation transition
  .deviation-fade-enter-active {
    transition: all 0.3s;
  }

  .deviation-fade-enter {
    opacity: 0;
  }

  .deviation-fade-enter-to {
    opacity: 1;
  }

  .deviation-fade-leave-active {
    transition: all 0.3s;
  }

  .deviation-fade-leave {
    opacity: 1;
  }

  .deviation-fade-leave-to {
    opacity: 0;
  }

  .deviation-switch-type {
    color: #fff;
    cursor: pointer;
  }

  .deviation-bubble {
    position: absolute;
    top: -12px;
    right: -24px;
    height: 20px;
    width: 20px;
    display: flex;
    font-size: 12px;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    color: #fff;
    background-color: #FF454B;
    text-align: center;
  }

  .deviations-leave-active {
    transition: all 0.5s;
  }

  .deviations-leave {
    opacity: 1;
    width: 200px;
    overflow: hidden;
  }

  .deviations-leave-to {
    opacity: 0;
    width: 0;
  }

  .deviation-dialog {
    width: 100%;
    min-height: 100px;
    background-color: #fff;
    border-radius: 8px;
    padding: 16px 32px;
  }

  .deviation-dialog-title {
    font-size: 22px;
    font-weight: 600;
    color: #363636;
    margin-bottom: 4px;
  }

  .deviation-dialog-sub-title {
    font-size: 12px;
    color: #aaa;
    margin-bottom: 8px;
  }

  .deviation-dialog-textarea {
    width: 100%;
    background-color: #f4f4f4;
    height: 140px;
    resize: none;
    border-radius: 6px;
    padding: 12px;

    &:focus {
      outline: none;
    }
  }

  .deviation-dialog-button-box {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  .deviation-total-count {
    margin-left: 24px;
    color: #fff;
    font-size: 13px;
  }

  .no-tasks-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    font-size: 26px;
  }

  .loading-container {
    display: flex;
    height: 400px;
    justify-content: center;
    align-items: center;
  }

</style>
